import { useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { Row, Col, Visible, Hidden } from 'react-grid-system';
import * as Yup from 'yup';
import ImageUploading from 'react-images-uploading';
import { useReturnContext } from '../../../contexts/returnContext';
import { useTranslation } from '../../../contexts/translationContext';
import {
  constructNumberArray,
  getValueLabelFromNumber,
  tImage,
  tReturnForm,
  tReturnFormData,
} from '../../../contexts/zod';
import { useReturnReasons, Ioption } from '../../../hooks/useReturnReasons';
import Resize from '../../../utils/base64-resizer';
import Spacing from '../../atoms/Spacing';
import LoadingSpinner from '../../molecules/LoadingSpinner';
import PrimaryButton from '../../molecules/PrimaryButton';
import SecondaryButton from '../../molecules/SecondaryButton';
import TextArea from '../../molecules/TextArea';
import FormikDropdown from './FormikDropdown';
import useStatusMessage from '../../../hooks/useStatusMessage';
import { theme } from '../../../utils/theme';
import IconButton from '../../molecules/IconButton';
import Typography from '../../atoms/Typography';

function topFunction() {
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
}

const ReturnForm: FC<tReturnForm> = ({ setOpen, layout, ...props }) => {
  const returnData = props.returnData || undefined;

  const { productNumber, quantity } = props;
  const { reasons, loading } = useReturnReasons(productNumber);
  const { addToReturns, updateToReturns, uploadImage, deleteImage } = useReturnContext();
  const { translate } = useTranslation();
  const { numberOfItems } = useStatusMessage(props);
  const [uploadingImages, setUploadingImages] = useState<boolean>(false);
  const [imagesToDelete, setImagesToDelete] = useState<tImage[]>([]);
  const [selectedReason, setSelectedReason] =
    useState<Ioption>({} as Ioption);

  const maxFileSize = 1e+7; // 10MB
  const maxNumberOfImages = 3;

  const initialValues: tReturnFormData = React.useMemo(() => ({
    amount: returnData?.amount || undefined,
    reason: returnData?.reason || '',
    returnDescription: returnData?.returnDescription || '',
    images: returnData?.images || [],
  }), [returnData]);

  useEffect(() => {
    if (returnData?.reason) {
      setSelectedReason(reasons.find((item) => item.value === returnData.reason));
    }
  }, [reasons, returnData?.reason]);

  // eslint-disable-next-line consistent-return
  const getMaxValue = () => {
    if (returnData && quantity - numberOfItems === 0) {
      return returnData.amount;
    }
    if (returnData && quantity - numberOfItems !== 0) {
      return returnData.amount + quantity - numberOfItems;
    }
  };

  const min = 1;
  const max = getMaxValue();

  const x = Array.from({ length: max - min + 1 }, (_, i) => i + min);

  const returnableQuantity =
    returnData && returnData.reason
      ? getValueLabelFromNumber(x)
      : getValueLabelFromNumber(constructNumberArray(quantity - numberOfItems));

  const validationSchema = Yup.object({
    amount: Yup.number().required(
      `${translate('quantity', null)} ${translate(
        'is_required',
        null,
      ).toLowerCase()}`,
    ),
    reason: Yup.string().required(
      `${translate('return_reason', null)} ${translate(
        'is_required',
        null,
      ).toLowerCase()}`,
    ),
    returnDescription: Yup.string(),
  });

  const selectLayoutOption = () => {
    if (layout === 'orderLine') {
      return (
        <PrimaryButton
          disabled={uploadingImages}
          loading={uploadingImages}
          type="submit"
          icon="package-plus"
          translateKey="add_return"
        />
      );
    }
    return (
      <Row gutterWidth={24}>
        <Hidden xs sm>
          <Col />
        </Hidden>
        <Col xs={12} sm="content">
          <Visible xs>
            <Spacing spacing={1} />
          </Visible>
          <SecondaryButton
            type="button"
            onClick={() => setOpen(false)}
            translateKey="cancel"
          />
        </Col>

        <Col xs={12} sm="content">
          <Visible xs>
            <Spacing spacing={1} />
          </Visible>
          <PrimaryButton
            disabled={uploadingImages}
            loading={uploadingImages}
            type="submit"
            icon="package-plus"
            translateKey="update_return"
          />
        </Col>
      </Row>
    );
  };

  const getReadableReturnReason = (values: tReturnFormData) =>
    reasons.find((item) => item.value === values.reason).label;

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values: tReturnFormData) => {
      if (selectedReason?.canUploadPhoto &&
        selectedReason?.requiresPhoto &&
        values.images?.length === 0) {
          return;
      }

      if (!selectedReason?.canUploadPhoto) {
        formik.setFieldValue('images', []);
      }

      if (imagesToDelete.length > 0) {
        imagesToDelete.forEach(async (image) => {
          await deleteImage(image.reference);
          imagesToDelete.splice(imagesToDelete.indexOf(image), 1);
        });
      }

      setUploadingImages(true);
      formik.setFieldValue('images', await values.images?.reduce(async (imagesP, image) => {
        Promise.resolve();
        const images = await imagesP;

        if (!image.reference) {
          // eslint-disable-next-line no-param-reassign
          image.dataURL = await Resize(image.dataURL, 2);
          const { reference } = await uploadImage(image);

          if (image.dataURL) {
            values.images?.push({
              ...image,
              salesOrderLineId: props.salesOrderLineId,
              dataURL: image.dataURL as string,
              file: image.file,
              reference: reference as string,
            });
          }
        }

        return images;
      }, Promise.resolve([] as tImage[])));

      // eslint-disable-next-line no-param-reassign
      values.images = values.images?.filter((img) => img.reference !== undefined);
      setUploadingImages(false);

      if (layout === 'returnLine') {
        updateToReturns({
          ...props,
          returnData: {
            ...values,
            readableReason: getReadableReturnReason(values),
          },
          initialReturnReason: props.returnData.reason,
        });
        setOpen(false);
      } else {
        topFunction();
        addToReturns({
          ...props,
          returnData: {
            ...values,
            readableReason: getReadableReturnReason(values),
          },
        });
        setOpen(false);
      }
    },
  });

  if (loading) {
    return (
      <Row align="center" justify="center">
        <LoadingSpinner color={theme.colors.grey[80]} />
      </Row>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Row>
        <Col xs={12} lg={8}>
          <Row gutterWidth={32}>
            <Col xs={12} md={4} lg={4}>
              <FormikDropdown
                name="amount"
                error={formik.touched.amount && formik.errors.amount}
                translateKey="select_quantity"
                options={returnableQuantity}
                onChange={(data) =>
                  formik.setFieldValue('amount', parseInt(data.value, 10))
                }
                value={formik.values.amount?.toString()}
                className={undefined}
              />
            </Col>
            <Col xs={12} md={8} lg={8}>
              <FormikDropdown
                name="reason"
                error={formik.touched.reason && formik.errors.reason}
                translateKey="placeholder_dropdown_reasons"
                options={reasons}
                onChange={(data) => {
                  formik.setFieldValue('reason', data.value);
                  setSelectedReason(data);
                }}
                value={formik.values.reason}
                className={undefined}
              />
            </Col>
            <Col>
              <Spacing spacing={3} />
              <TextArea
                value={formik.values.returnDescription}
                label="add_comment"
                name="returnDescription"
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
        </Col>
        <Col xs={12} lg={4}>
          { selectedReason?.canUploadPhoto && (
          <ImageUploading
            multiple
            maxFileSize={maxFileSize}
            acceptType={['jpg', 'jpeg', 'png']}
            maxNumber={maxNumberOfImages}
            value={formik.values.images}
            onChange={(data) => {
              const images = data.reduce((uniqueImages, image) => {
                const isDuplicate = uniqueImages.find(
                  (uniqueImage) =>
                    uniqueImage.dataURL === image.dataURL &&
                    uniqueImage.salesOrderLineId === props.salesOrderLineId,
                );

                if (!isDuplicate && image.dataURL) {
                  uniqueImages.push({
                    ...image,
                    salesOrderLineId: props.salesOrderLineId,
                    dataURL: image.dataURL as string,
                    file: image.file,
                    reference: image.reference as string | undefined,
                  });
                }

                return uniqueImages;
              }, [] as tImage[]);
              formik.setFieldValue('images', images);
            }}
          >
            {({
              imageList,
              onImageUpload,
              onImageRemove,
              dragProps,
              errors,
            }) => (
              <div className="upload__image-wrapper">
                <div style={{ display: 'flex', marginTop: '18px' }}>
                  <div style={{ marginRight: '10px' }}>
                    <IconButton
                      icon="add-photo"
                      iconColor="#ffffff"
                      backgroundColor="#000000"
                      onClick={onImageUpload}
                      {...dragProps}
                    />
                  </div>
                  <div>
                    <Typography translateKey="upload_images_title" variant="h5" />
                    <Typography translateKey="upload_images_subtitle" variant="body3" />
                    {errors?.maxFileSize && <Typography translateParams={{ filesize: maxFileSize / 1e+6 }} translateKey="upload_images_filesize_error" color="red" variant="body3" />}
                    {errors?.acceptType && <Typography translateKey="upload_images_filetype_error" color="red" variant="body3" />}
                    {errors?.maxNumber && <Typography translateParams={{ count: maxNumberOfImages }} translateKey="upload_images_count_error" color="red" variant="body3" />}
                    {selectedReason?.requiresPhoto && formik.submitCount > 0 && imageList.length <= 0 && <Typography translateKey="upload_images_required_error" color="red" variant="body3" />}
                  </div>
                </div>
                <Spacing spacing={3} />
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'start' }}>
                  {imageList.map((image, index) => (
                    <div style={{ position: 'relative', marginRight: 10 }} key={image.file?.name} className="image-item">
                      <img src={image.dataURL} alt="" width="80" />
                      <div style={{ top: 0, left: 0, position: 'absolute' }}>
                        <IconButton
                          icon="minus"
                          iconColor={theme.colors.grey[100]}
                          backgroundColor={theme.colors.grey[40]}
                          onClick={() => {
                            const imageToDelete = formik.values.images?.find(
                              (img) => img.dataURL === imageList[index].dataURL);
                            if (imageToDelete?.reference) {
                              setImagesToDelete(
                                [...imagesToDelete, imageToDelete],
                              );
                            }
                            onImageRemove(index);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ImageUploading>
          )}
        </Col>
        <Col>
          <Spacing spacing={3} />
          {selectLayoutOption()}
        </Col>
      </Row>
    </form>
  );
};

export default ReturnForm;
